'use strict';

import { BaseElement } from '../interfaces/BaseElement';
import { settingsData } from '../../settings';
import { findSelectionAndCrmData, numberWithCommas } from '../../functions';
import { CardTypes } from '../../common';
import {changeLangToCurrent, getCurrentLang} from '../components/langSwitcher/langSwitcher';

const DEFAULT_SETTINGS = {
    price2: false,
    salePrice: true,
    rentPrice: false
};

export class Price {
    constructor () {
        const prices = config.features.apartments.price;
        if (prices) {
            this.params = prices;
        } else {
            this.params = DEFAULT_SETTINGS;
        }

        this.show = Boolean((config.property.settings.displayPrice === 'show' ||
            config.property.settings.displayPrice === 'button' ||
            settingsData.isConferenceHost) && this.#priceIsPresentInCRM());

        this.currency = config.crmProjectSettings.localization.currencyLabel;
        this.priceButton = config.property.settings.displayPrice === 'button';
        this.items = {};
    }

    #priceIsPresentInCRM() {
        let isPricePresent = false;

        for (const priceType in this.params) {
            if (!this.params[priceType]) continue;

            isPricePresent = settingsData.allCrmData.some((item) => {
                return item[priceType] > 0;
            });

        }

        return isPricePresent;
    }

    addBlock(parent) {
        this.parent = parent;
        this.container = new BaseElement()
            .addClass('price')
            .appendTo(this.parent)
            .DOMElement;
        this.wrapper = new BaseElement()
            .addClass('price__wrapper')
            .appendTo(this.container)
            .DOMElement;

        Object.entries(this.params).forEach(([priceType,enabled]) => {
           if (enabled) {
               const item = Price.createPriceItem(this.wrapper,priceType);
               const itemDom = item.DOMElement;
               if (priceType === 'price2') {
                   new BaseElement('span')
                       .addClass('language-string', 'price-type', 'price2Title')
                       .addAttribute('data-dictionary','price2Title')
                       .innerHtml(changeLangToCurrent('price2Title'))
                       .appendTo(itemDom);
               }

               const wrapper = new BaseElement()
                   .addClass('card__price-wrapper')
                   .appendTo(itemDom);
               const price = new BaseElement('span')
                   .addClass( 'card__price-value', 'translate-number', 'price')
                   .appendTo(wrapper.DOMElement);
               const currency = new BaseElement('span')
                   .addClass( 'card__price-currency', 'language-string')
                   .addAttribute('data-dictionary', this.currency)
                   .innerHtml(changeLangToCurrent(this.currency))
                   .appendTo(wrapper.DOMElement);
               const currencyAdditional = new BaseElement('span')
                   .addClass('card__currency-additional', 'language-string')
                   .addAttribute('data-dictionary', 'currencyAdditional')
                   .innerHtml(changeLangToCurrent('currencyAdditional'))
                   .appendTo(wrapper.DOMElement);

               const priceLabelData = config.crmProjectSettings.property.settings.priceLabel;
               if (priceLabelData['en'] === '') {
                   this.items[priceType] = {
                       item,
                       price,
                       currency,
                       currencyAdditional
                   }
               } else {
                   const label = new BaseElement('span')
                     .addClass('price-label', 'language-string', 'hidden')
                     .innerHtml(priceLabelData[getCurrentLang()])
                     .appendTo(this.wrapper);
                   for (const key in priceLabelData) {
                       label.addAttribute(`data-lang-${key}`, priceLabelData[key])
                   }

                   this.items[priceType] = {
                       item,
                       price,
                       currency,
                       currencyAdditional,
                       label
                   }
               }
           }
        });

        this.addPriceButton(parent);

        return this;
    }

    setPrice(crmOrEngineID) {
        if (!this.parent || !this.container) {
            console.error('Price error: Add price block first');
            return this;
        }

        const data = typeof crmOrEngineID === 'string' ? findSelectionAndCrmData(crmOrEngineID).crmDataOfFlat : crmOrEngineID;
        let cardType = null;
        let card = null;
        let element = null;

        if (this.parent.classList.contains('flat-tooltip__price-value')) {
            card = this.parent.closest('.flat-tooltip');
            cardType = 'flat-tooltip';
        } else if (this.parent.classList.contains('similar-card__price')) {
            card = this.parent.closest('.similar-card');
            cardType = CardTypes.Similar;
        } else if (this.parent.classList.contains('search__additional-content')) {
            cardType = 'search-item';
        } else if (this.parent.classList.contains('apartment-card__price')) {
            const elementsWithTranslate = [...this.parent.querySelectorAll('.translate-number')];
            elementsWithTranslate.forEach(element => {
                element.classList.remove('translate-number');
            });
            cardType = 'mobile-item';
        } else if (this.parent.classList.contains('cylinder-building-label__item')) {
            cardType = 'cylinder-label';
        } else {
            card = this.parent.closest('.card');
            cardType = card.classList.contains(`${CardTypes.Options}-card`) ? CardTypes.Options : CardTypes.Unit;
            this.container.classList.add(`${cardType}-card__price`,'card__price');
        }

        if (data.status === 'Available' && this.showPrice) {
            const prices = this.getPrices(data);
            Object.keys(this.items).forEach(priceType => {
               if (prices[priceType]) {
                   element = this.items[priceType];
                   const priceValue = Price.prettier(prices[priceType]);
                   if (Boolean(priceValue)) {
                       if (card)
                           card.classList.remove('unavailable');
                       element.item
                           .removeClass('unavailable')
                           .addClass('ready-to-show');

                       this.itemVisibility(priceType,'show');

                       if (prices['price2'] && priceType !== 'price2') {
                           element.item.addClass('old-price');
                       } else {
                           element.item.removeClass('old-price');
                       }

                       element.price
                           .addClass(`${cardType}-card__price-value`)
                           .removeClass('language-string')
                           .deleteAttribute('data-dictionary')
                           .innerHtml(priceValue);

                       element.currency
                           .addAttribute('data-dictionary',this.currency)
                           .addClass('language-string')
                           .innerHtml(changeLangToCurrent(this.currency));

                       element.currencyAdditional
                           .addAttribute('data-dictionary', 'currencyAdditional')
                           .addClass('language-string')
                           .innerHtml(changeLangToCurrent('currencyAdditional'));

                   } else {
                       this.itemVisibility(priceType,'hide');
                       element.item
                           .removeClass('ready-to-show');
                   }
               } else {
                   element = this.items[priceType];
                   element.item
                       .removeClass('ready-to-show');
                   this.itemVisibility(priceType,'hide');
               }
            });
        } else if (data.status !== 'Available') {
            if (card)
                card.classList.add('unavailable');
            Object.keys(this.items).forEach(priceType => {
               element = this.items[priceType];
               if (priceType === 'salePrice') {
                   element.item
                       .addClass('unavailable')
                       .addClass('ready-to-show')
                       .removeClass('old-price');

                   this.itemVisibility(priceType,'show');

                   element.price
                       .removeClass('translate-number')
                       .innerHtml(changeLangToCurrent(data.status))
                       .addAttribute('data-dictionary', data.status)
                       .addClass(`${cardType}-card__price-value`,'language-string');

                   element.currency
                       .deleteAttribute('data-dictionary')
                       .removeClass('language-string')
                       .innerHtml('');

                   element.currencyAdditional
                       .deleteAttribute('data-dictionary')
                       .removeClass('language-string')
                       .innerHtml('');
               } else {
                   element.item
                       .removeClass('ready-to-show')
                   this.itemVisibility(priceType,'hide');
               }
            });
        } else {
           this.pricesToggle('hide');
        }
        return this;
    }

    addPriceButton(parent) {
        let button;

        if (this.priceButton) {
            let isAvailable;
            const isParentTooltip = parent.classList.contains('flat-tooltip__price-value');
            if (isParentTooltip) {
                isAvailable = false;
            } else {
                const engineId = parent.closest('[data-engineId]').getAttribute('data-engineId');
                const crmApartmentData = settingsData?.allCrmData.find(item => item.engineId === engineId);
                isAvailable = crmApartmentData.status === 'Available';
            }

            const scope = this;
            let state = 'hide';
            button = new BaseElement()
                .addClass(
                  'card__price-show-price-btn',
                  'language-string',
                  'button',
                  'cards-buttons')
                .addAttribute('data-dictionary', 'show price')
                .innerHtml(changeLangToCurrent('show price'))
                .event('click', function (e) {
                    e.stopPropagation();
                    state = state === 'hide' ? 'show' : 'hide';
                    this.classList.toggle('hide');
                    this.classList.toggle('hidden');
                    scope.pricesToggle(state,true);
                })
                .appendTo(this.wrapper);

            if (isAvailable) {
                this.pricesToggle(state);
            } else {
                button.addClass('hide');
                button.addClass('hidden');
                button.DOMElement.closest('.price__wrapper').querySelector('.price-item').classList.remove('hidden');
                this.pricesToggle('show');
            }
        }
        return button;
    }

    pricesToggle(state,buttonClicked = false) {
        Object.keys(this.items).forEach(priceType => {
            this.itemVisibility(priceType,state,buttonClicked);
        });
    }

    getPrices(data) {
        const prices = {};
        Object.entries(this.params).forEach(([priceType,enabled]) => {
            if (enabled) {
                prices[priceType] = parseInt(data[priceType]);
            }
        });

        if (prices.price2 && prices.price2 === prices.salePrice) {
            delete prices.price2;
        }

        return prices;
    }

    itemVisibility(priceType,state,priceButtonClicked = false) {
        if (this.priceButton && !priceButtonClicked)
            return;

        const element = this.items[priceType];
        if (state === 'show' && element.item.DOMElement.classList.contains('ready-to-show')) {
            element.item.removeClass('hidden');
            if (!element.item.DOMElement.classList.contains('unavailable'))
                element.label && element.label.removeClass('hidden');
        } else {
            element.item.addClass('hidden');
            element.label && element.label.addClass('hidden');
        }
    }

    static createPriceItem(parent,priceType) {
        return new BaseElement()
            .addClass('price-item',priceType,'hidden')
            .appendTo(parent)
    }

    static prettier(value) {
        if (value > 0) {
            return numberWithCommas(value, true);
        }
        return '';
    }

    get showPrice() {
        return this.show;
    }
}
